import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
} from '@backstage/core-components';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import { SidebarLogo } from './SidebarLogo';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import { usePermission } from '@backstage/plugin-permission-react';
import { policyEntityCreatePermission } from '@backstage-community/plugin-rbac-common';
import { cloneElement, isValidElement, ReactNode } from 'react';
import { useContentPages } from '../contentPages/useContentPages';
import { resolveIcon } from '../contentPages/widgets';
import { useFeatureFlag } from '../featureFlags';

/**
 * Force every nav-item icon to render at the same visual size,
 * regardless of who authored the source plugin.
 *
 * The problem this solves:
 *   `SidebarItem` from `@backstage/core-components` renders the icon
 *   prop with `<Icon fontSize="inherit" />`. Upstream nav items
 *   declared with `NavItemBlueprint.make({ icon: () => jsx(AppIcon, {
 *   fontSize: "inherit", id: "kind:api" }) })` honour that — the
 *   icon inherits the container's `font-size: theme.typography.fontSize`
 *   (~14px) so MUI's `width: 1em` SVG renders at ~14px and the
 *   sidebar's CSS rule `.itemIcon & svg { width: 1.5em }` scales it to
 *   ~21px.
 *
 *   But our custom modules (catalogModule, apiDocsModule,
 *   techdocsModule, kindsModule) declare icons as plain JSX elements
 *   on `PageBlueprint.params.icon` (e.g. `<HomeIcon />`,
 *   `<LibraryBooksIcon />`). The auto-derived nav item stores the
 *   element as-is, with no `fontSize` prop set, so MUI defaults to
 *   `fontSize="medium"` = `1.5rem` = 24px. Sidebar's JSS rule loses
 *   the specificity battle against MUI's `.MuiSvgIcon-fontSizeMedium`
 *   class on certain icons (notably AppIcon and LibraryBooksIcon),
 *   producing ~28-32px icons next to the standard ~21px ones — the
 *   "APIs" and "Docs" entries visibly tower over Home/Catalog/etc.
 *
 *   `cloneElement` with an inline `style.fontSize` wins on
 *   specificity (inline > JSS > stylesheet) AND propagates to the
 *   SVG inside MUI icons (MUI's SvgIcon forwards `style` down). So
 *   we can pin every icon to the exact same size in one place
 *   without touching each module.
 */
const NormalizedIcon = ({ icon }: { icon: ReactNode }) => {
  if (!isValidElement(icon)) {
    return <>{icon}</>;
  }
  const original = icon as React.ReactElement<{
    style?: React.CSSProperties;
    fontSize?: 'inherit' | 'small' | 'medium' | 'large' | 'default';
  }>;
  return cloneElement(original, {
    fontSize: 'inherit',
    style: {
      fontSize: '1.25rem',
      width: '1.25rem',
      height: '1.25rem',
      ...(original.props.style ?? {}),
    },
  });
};

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: ({ navItems }) => {
      const nav = navItems.withComponent(item => (
        <SidebarItem
          icon={() => <NormalizedIcon icon={item.icon} />}
          to={item.href}
          text={item.title}
        />
      ));

      /*
       * RBAC sidebar gating — hide admin/governance surfaces from
       * users who can't actually use them.
       *
       * `policyEntityCreatePermission` is the basic (non-resource)
       * permission used by the upstream RBAC plugin's
       * `RequirePermission` wrapper around the Create-role route. In
       * our `rbac/policies.csv` ONLY the `admin` role has it (and
       * `superUsers` defined in `app-config.yaml > permission.rbac.admin.superUsers`
       * bypass every check), so this is effectively an "is admin?"
       * gate without having to introduce a new permission.
       *
       * NB: we considered `policyEntityReadPermission` here, but
       * that one is a RESOURCE permission (`resourceType:
       * "policy-entity"`). `usePermission` short-circuits resource
       * permissions to DENY when no `resourceRef` is supplied (see
       * `node_modules/@backstage/plugin-permission-react/dist/hooks/usePermission.esm.js`),
       * so it would always hide the items — including for admins.
       * `policyEntityCreatePermission` has no `resourceType` so it
       * resolves cleanly.
       *
       * Note that this only HIDES items from the sidebar — the
       * pages themselves still enforce permissions server-side
       * (`/rbac` calls `rbacApi.getUserAuthorization()` and renders
       * a 403 page on deny; CreateRole/EditRole sub-routes are
       * already wrapped in `<RequirePermission>`). So even if a
       * non-admin somehow lands on `/rbac` directly via URL, they
       * still see the 403 page, not the role list. The sidebar
       * gate is purely UX cleanup — fewer dead-end clicks for
       * non-admins.
       */
      const { allowed: canAdminPermissions } = usePermission({
        permission: policyEntityCreatePermission,
      });

      // `digitalFactory.features.*` master switches — hides nav entries
      // for disabled features on top of the existing permission gating
      // above. See `packages/app/src/modules/featureFlags.ts`.
      const governanceFeatureEnabled = useFeatureFlag('governance');
      const contentPagesFeatureEnabled = useFeatureFlag('contentPages');

      // Skipped items
      nav.take('page:search'); // Using search modal instead

      /*
       * The content-pages plugin registers a single catch-all
       * `PageBlueprint` at `/p/:slug` which would auto-emit a generic
       * "Content Page" entry in the sidebar (placeholder slug, useless
       * link). Take it out unconditionally so the framework's auto-nav
       * doesn't add a dead item — we render ONE `SidebarItem` per
       * actual `ContentPage` entity from the catalog below.
       */
      nav.take('page:content-pages');

      /*
       * Live list of content-page entities (Components with
       * `spec.type: content-page`). The hook caches the catalog query
       * via `useAsync`, so navigating between pages doesn't refetch.
       * While loading we render nothing — the rest of the sidebar
       * stays interactive, and the items pop in as soon as the catalog
       * answers (typically < 100ms after first paint).
       */
      const { entities: contentPages } = useContentPages();

      /*
       * Take admin items unconditionally so they don't fall through
       * into the Menu group's `nav.rest({ sortBy: 'title' })` — if
       * we only took them inside `{canAdminPermissions && ...}`,
       * `nav.take()` would short-circuit and leave them in the nav
       * pool, making "RBAC" / "Permissions reference" / "Visualizer"
       * pop up under Kinds Guide for non-admins. Always-take
       * + conditionally-render is the only correct combination.
       */
      nav.take('page:rbac');
      nav.take('page:permissions-guide');
      nav.take('page:notifications-admin');
      const visualizerItem = nav.take('page:app-visualizer');
      const governanceItem = nav.take('page:governance');

      return (
        <Sidebar>
          <SidebarLogo />
          <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
            <SidebarSearchModal />
          </SidebarGroup>
          <SidebarDivider />
          {/*
           * MAIN MENU — the primary surfaces every user touches.
           * Order matters here: Home → Catalog → Create stays pinned
           * up top so the muscle memory holds even when new plugins
           * are installed and start contributing nav items into
           * `nav.rest()`. After the divider the auto-discovered
           * items flow in alphabetical order (APIs, Docs, Kinds
           * Guide, etc).
           *
           * The catalog page is served by our own `catalogModule`
           * (pluginId: 'catalog-override') instead of the upstream
           * `@backstage/plugin-catalog/alpha` page, which is
           * disabled in `app-config.yaml` because its `<HeaderPage>`
           * from `@backstage/ui` stacks an extra white strip below
           * our blue `BoschPageLayout` banner. Same story for
           * `apiDocsModule` (pluginId: 'api-docs-override'), but
           * that one falls naturally into `nav.rest(...)` below
           * because we don't need to pin it.
           */}
          <SidebarGroup label="Menu" icon={<MenuIcon />}>
            {nav.take('page:home')}
            {nav.take('page:catalog-override')}
            {/*
             * `nav-item:scaffolder-override` (pluginId 'scaffolder-override')
             * is our sidebar entry for `/create`. The upstream page stays
             * enabled; only `sub-page:scaffolder/templates` is replaced so
             * admin-tagged templates stay hidden from non-admins — see
             * `packages/app/src/modules/scaffolder/index.tsx` for the full
             * rationale. The upstream `nav-item:scaffolder` is disabled in
             * `app-config.yaml` so this is the only "Create..." entry rendered.
             */}
            {nav.take('nav-item:scaffolder-override')}
            <SidebarScrollWrapper>
              {nav.rest({ sortBy: 'title' })}
              {/*
               * Admin-authored content pages (Components with
               * spec.type: content-page) live INSIDE the scroll
               * wrapper, sorted alphabetically by the admin-chosen
               * sidebar label. They sit after `nav.rest()` so they
               * never displace static plugin nav items — visually
               * they pile up at the BOTTOM of the auto-discovered
               * group, which matches the mental model "these are
               * the team's own pages, added on top of the platform
               * defaults".
               *
               * Sidebar label, icon and href are all read from
               * the entity annotations (see
               * `catalog/templates/new-content-page/content/
               * catalog-info.yaml.skeleton` for the canonical shape).
               * `metadata.name` doubles as the URL slug — see the
               * filter rationale in `useContentPages.tsx`.
               */}
              {(contentPagesFeatureEnabled ? [...contentPages] : [])
                .sort((a, b) => {
                  const labelA =
                    a.metadata.annotations?.[
                      'bosch.io/content-page-sidebar-label'
                    ] ??
                    a.metadata.title ??
                    a.metadata.name;
                  const labelB =
                    b.metadata.annotations?.[
                      'bosch.io/content-page-sidebar-label'
                    ] ??
                    b.metadata.title ??
                    b.metadata.name;
                  return labelA.localeCompare(labelB);
                })
                .map(entity => {
                  const annotations = entity.metadata.annotations ?? {};
                  const label =
                    annotations['bosch.io/content-page-sidebar-label'] ??
                    entity.metadata.title ??
                    entity.metadata.name;
                  const iconName =
                    annotations['bosch.io/content-page-sidebar-icon'];
                  return (
                    <SidebarItem
                      key={entity.metadata.name}
                      icon={() => (
                        <NormalizedIcon icon={resolveIcon(iconName)} />
                      )}
                      to={`/p/${entity.metadata.name}`}
                      text={label}
                    />
                  );
                })}
            </SidebarScrollWrapper>
          </SidebarGroup>
          <SidebarSpace />
          <SidebarDivider />
          <NotificationsSidebarItem />
          <SidebarDivider />
          <SidebarGroup
            label="Settings"
            icon={<UserSettingsSignInAvatar />}
            to="/settings"
          >
            {/*
             * RBAC admin UI — only renders for users with policy.entity.create.
             * Sits in Settings because it's an admin/governance surface.
             * "Permissions reference" sits IMMEDIATELY below RBAC on purpose:
             * /rbac is the live editor (where you actually grant roles),
             * /permissions-guide is the static cheat-sheet (what the
             * bootstrap defaults look like, which permission belongs to
             * which role, etc). Visually pairing them makes it obvious
             * that they are two sides of the same feature — see the long
             * comment in `modules/permissions/index.tsx` for why we did
             * not merge them into a single tabbed page.
             *
             * The Visualizer (page:app-visualizer) is the framework's
             * extension graph — a developer/admin debugging tool from
             * `@backstage/plugin-app-visualizer`. We hide it from
             * non-admins for the same UX reason: keeps a layperson
             * sidebar focused on user-facing features, not internals.
             *
             * `page:user-settings` is ALWAYS shown — every signed-in
             * user has a profile, theme picker, and connected-auth
             * page they need to access regardless of role. That also
             * keeps the Settings group label from rendering empty
             * for non-admins (which would look like a UI bug).
             */}
            {canAdminPermissions && governanceFeatureEnabled && governanceItem}
            {canAdminPermissions && visualizerItem}
            {nav.take('page:user-settings')}
          </SidebarGroup>
        </Sidebar>
      );
    },
  },
});
